import { inject, Injectable, InjectionToken, isDevMode } from '@angular/core';
import { BehaviorSubject, firstValueFrom, from, Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { TranslocoService } from '@jsverse/transloco';
import { DateAdapter } from '@angular/material/core';
import { registerLocaleData } from '@angular/common';
import { Locale } from 'date-fns';
import { fr as dateFnsFr } from 'date-fns/locale';
import localeFr from '@angular/common/locales/fr';
import localeFrExtra from '@angular/common/locales/extra/fr';

export interface LocaleConfig {
  locales: AppLang[];
  defaultLocale: AppLang;
  /** query param name to use when retrieving default locale, default to undefined */
  queryParam: string | undefined;
  /**
   * Locale demandée explicitement via `queryParam`, résolue au bootstrap.
   *
   * Prioritaire sur la locale mémorisée en session : une app qui arrive avec une langue dans son
   * URL doit s'afficher dans cette langue, même si l'onglet a déjà visité l'app dans une autre.
   */
  forcedLocale?: AppLang;
}

export const DEFAULT_CONFIG = new InjectionToken<LocaleConfig>('DEFAULT_LOCALE_CONFIG');

export type AppLang = 'en' | 'fr';

/**
 * Encapsulates the current locale and handles locale change across an application.
 *
 * LocaleService must be initialized via `provideLocale()`.
 *
 * This service manages to:
 *
 * - angular locale
 * - angular material dates
 * - date-fns
 * - transloco i18n
 * - ngx-translate
 *
 * It could be possible to minimize coupling between all those systems (i.e.
 * the previous systems could listen to language$ Observable, but atm it seems
 * to be overkill since all our apps rely on the same libraries).
 */
@Injectable()
export class LocaleService {
  private readonly translocoService = inject(TranslocoService);
  private readonly dateAdapter = inject(DateAdapter, { optional: true });
  private readonly localeConfig = inject(DEFAULT_CONFIG);
  private initDone = false;

  private readonly lang$ = new BehaviorSubject<AppLang>('fr');
  readonly language$ = this.lang$.asObservable();

  private readonly dateFnsLocale$ = new BehaviorSubject<Locale | undefined>(undefined);

  /** Async initialization at bootstrap */
  initLanguage(): Promise<void> {
    const locale = LocaleService.getInitialLocale(this.localeConfig.defaultLocale, this.localeConfig.forcedLocale);
    return firstValueFrom(this.forceChangeLanguage(locale));
  }

  /** Current language getter */
  getCurrentLang(): AppLang {
    return this.lang$.value;
  }

  getCurrentDateFns(): Locale {
    const locale = this.dateFnsLocale$.getValue();
    if (locale == null) {
      throw new Error(`dateFnsLocale unavailable: LocaleService startup is not finished`);
    }
    return locale;
  }

  /**
   * Change language at runtime
   *
   * Changing the locale once the application is already initialized
   * trigger an application reload (this is the only way
   * to reevaluate angular date and currency pipes).
   */
  changeLanguage(lang: AppLang): Observable<void> {
    if (this.getCurrentLang() === lang) {
      return of(void 0);
    }
    if (!this.isSupported(lang)) {
      if (isDevMode()) {
        console.warn(`[LocaleService] Attempted to change to unsupported locale: ${lang}`);
      }
      return of(void 0);
    }
    return this.forceChangeLanguage(lang).pipe();
  }

  /** Change language at runtime */
  private forceChangeLanguage(lang: AppLang): Observable<void> {
    return from(
      Promise.all([
        // date-fns
        this.importDateFnsLocale(lang),
        // Angular locale (requires reloading the app to take effect)
        this.registerAngularLocale(lang),
      ]),
    ).pipe(
      tap(([dateFnsLocale]) => {
        // Transloco
        this.translocoService?.setActiveLang(lang);

        // Material DateAdapter (using DateFnsAdapter)
        this.dateAdapter?.setLocale(dateFnsLocale);

        // Save locale (locale change needs full app reload for angular date and currency pipes)
        sessionStorage.setItem('locale', lang);

        // Reload the app so Angular pipes pick up new LOCALE_ID
        if (this.initDone) {
          this.forgetLocaleQueryParam();
          location.reload();
        }

        this.initDone = true;
        this.lang$.next(lang);
        this.dateFnsLocale$.next(dateFnsLocale);
      }),
      map(() => void 0),
    );
  }

  /**
   * Retire de l'URL le query param de langue avant un rechargement.
   *
   * Ce param exprime la langue demandée à l'arrivée sur l'application et reste prioritaire sur la
   * locale mémorisée (cf. {@link LocaleConfig.forcedLocale}). Sans ce nettoyage, un changement de
   * langue explicite serait annulé par le rechargement qu'il déclenche lui-même, et toute
   * réapplication automatique de la langue relancerait un rechargement en boucle.
   */
  private forgetLocaleQueryParam(): void {
    const queryParam = this.localeConfig.queryParam;
    if (queryParam == null) {
      return;
    }

    const url = new URL(window.location.href);
    if (!url.searchParams.has(queryParam)) {
      return;
    }

    url.searchParams.delete(queryParam);
    history.replaceState(history.state, '', url.toString());
  }

  /** Register Angular locale data for pipes (dynamic import to keep bundle small) */
  private async registerAngularLocale(lang: AppLang): Promise<void> {
    switch (lang) {
      case 'en': {
        const locale = await import('@angular/common/locales/en');
        registerLocaleData(locale.default, 'en-US');
        break;
      }
      case 'fr':
      default: {
        // static import for fr locale (rationale: this is the most used locale)
        registerLocaleData(localeFr, 'fr', localeFrExtra);
        break;
      }
    }
  }

  // dynamic import to keep bundle small; returns object containing the date-fns locale
  private async importDateFnsLocale(lang: AppLang): Promise<Locale> {
    switch (lang) {
      case 'fr':
        // static import for fr locale (rationale: this is the most used locale)
        return dateFnsFr;
      case 'en':
      default:
        return import('date-fns/locale/en-US').then(m => m.enUS);
    }
  }

  private isSupported(lang: AppLang) {
    return this.localeConfig.locales.includes(lang);
  }

  static getInitialLocale(defaultLocale: AppLang, forcedLocale?: AppLang): AppLang {
    return forcedLocale ?? (sessionStorage.getItem('locale') as AppLang) ?? defaultLocale;
  }
}
